import React, { useCallback, useEffect, useRef } from 'react';
import styles from './styles/UserSwap.module.scss';

import { OmaLummeApp } from '../../omaLummeContext';

import MobileMenuFooter from './MobileMenuFooter';

// Types.
import { SimpleUser } from '../../types/customer';
import { exoveSubscribe } from '../../actions/customerData';

type UserSwapProps = {
  nav: {
    navOpen: boolean;
    toggleNavOpen: (value: React.SetStateAction<boolean>) => void;
  };
  userSwap: {
    userSwapOpen: boolean;
    toggleUserSwapOpen: (value: React.SetStateAction<boolean>) => void;
  };
};

const UserSwap: React.FunctionComponent<UserSwapProps> = (props: UserSwapProps) => {
  const dispatch = OmaLummeApp.useDispatch();

  const { users } = OmaLummeApp.useState();

  const { toggleNavOpen } = props.nav;
  const { userSwapOpen, toggleUserSwapOpen } = props.userSwap;

  const escFunction = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        toggleUserSwapOpen(false);
      }
    },
    [toggleUserSwapOpen],
  );

  useEffect(() => {
    document.addEventListener('keydown', escFunction, false);

    return () => {
      document.removeEventListener('keydown', escFunction, false);
    };
  }, [escFunction]);

  function useOutsideAlerter(
    ref: React.RefObject<any>,
    open: {
      userSwapOpen: boolean;
      toggleUserSwapOpen: (value: React.SetStateAction<boolean>) => void;
    },
  ) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target)) {
          open.toggleUserSwapOpen(false);
        }
      }

      // Bind the event listener
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref, open]);
  }

  const userSwapRef = useRef<any>(null);
  useOutsideAlerter(userSwapRef, { userSwapOpen, toggleUserSwapOpen });

  const availableUsers: SimpleUser[] = [];
  const updateCurrentUser = (suppliedUserId: number): void => {
    let userToUpdate = users.parentUser;
    if (users.childUsers) {
      for (const child of users.childUsers) {
        if (child.userId === suppliedUserId) {
          userToUpdate = child;
        }
      }
    }

    // To fix issues with eliq they need all their entries in sessionStorage to be cleared
    // when we change user.
    Object.keys(sessionStorage).forEach(function (key) {
      if (key.includes('client-lumme-api')) {
        sessionStorage.removeItem(key);
      }
    });

    dispatch([
      'changeUsers',
      {
        ...users,
        activeUser: userToUpdate,
      },
    ]);
    dispatch(['changeContracts', undefined]);
  };

  useEffect(() => {
    async function subscribe() {
      await exoveSubscribe(users.activeUser.userId);
    }
    subscribe();
  }, [users.activeUser]);

  availableUsers.push(users.parentUser);

  if (users.childUsers) {
    for (const child of users.childUsers) {
      availableUsers.push(child);
    }
  }

  availableUsers.sort(function (a, b) {
    return a === users.activeUser ? -1 : b === users.activeUser ? 1 : 0;
  });

  const multipleUserOptions = availableUsers.length > 1;

  return (
    <div ref={userSwapRef} id="lumme-user-swap" className={`${styles.userSwap} ${userSwapOpen ? styles.active : ''}`}>
      <button
        id="choose-customership"
        className={`${styles.activeUser} ${multipleUserOptions ? (userSwapOpen ? styles.userListOpen : styles.userListClosed) : styles.singleUser}`}
        onClick={(): void => {
          if (document.getElementById('choose-customership')?.ariaDisabled === 'false') {
            toggleUserSwapOpen(!userSwapOpen);
            toggleNavOpen(false);
          }
        }}
        aria-expanded={userSwapOpen}
        aria-controls="lumme-user-swap-list"
        aria-disabled={!multipleUserOptions}
      >
        <div className={styles.availableUser}>
          <span className={styles.activeUserName}>{users.activeUser.userName}</span>
          <span className={styles.activeUserId}>{users.activeUser.userId}</span>
        </div>
      </button>
      {userSwapOpen && (
        <div className={styles.availableUsers} role="menu" id="lumme-user-swap-list">
          {availableUsers.map((user) => (
            <button
              className={`${styles.availableUser} ${user === users.activeUser ? styles.activeUser : ''}`}
              key={`${user.userName} ${user.userId}`}
              onClick={(): void => {
                updateCurrentUser(user.userId);
                toggleUserSwapOpen(false);
                userSwapRef.current.firstChild.focus();
              }}
              role="menuitem"
            >
              <span className={styles.activeUserName}>{user.userName}</span>
              <span className={styles.activeUserId}>{user.userId}</span>
            </button>
          ))}
          <MobileMenuFooter nav={props.nav} userSwap={props.userSwap} />
        </div>
      )}
    </div>
  );
};

export default UserSwap;
